'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  MapPin,
  TrendingUp,
  ShieldCheck,
  BadgeDollarSign,
  Sparkles,
  HelpCircle,
  ChevronDown,
  ArrowRight,
  Award,
  BookOpen,
  CheckCircle2,
} from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What is the Plotae Deal Score?',
    answer:
      'The Plotae Deal Score is a proprietary, data-driven rating system that helps investors, developers, and brokers quickly understand how strong a property deal is. It scores each land parcel out of 100 based on five core pillars: location, pricing, growth, legal status, and demand.',
  },
  {
    question: 'How is the score calculated?',
    answer:
      'Each property undergoes a rigorous evaluation under five main criteria: Location (max 25 pts), Price Advantage (max 20 pts), Growth & Development (max 20 pts), Legal & Documentation (max 20 pts), and Uniqueness & Demand (max 15 pts). Together, these ratings sum to a total possible score of 100 points.',
  },
  {
    question: 'What do the scores mean?',
    answer:
      "A score between 80–100 represents an 'Excellent Deal' (highly recommended with superb fundamentals). A score between 60–79 is a 'Good Deal' (solid investment value). A score between 40–59 indicates a 'Fair Deal' (warrants caution), while any score below 40 is classified as a 'Weak Deal' representing higher risks or overpriced land.",
  },
  {
    question: 'Why should I trust the Deal Score?',
    answer:
      'Our engine is completely transparent, unbiased, and data-driven. We cross-reference raw, real-time pricing benchmarks from the Dubai Land Department (DLD) and active market listings to evaluate true property value and market opportunities accurately.',
  },
  {
    question: 'Can two nearby plots have different scores?',
    answer:
      'Yes, absolutely. Even on the same street, individual plots can vary due to crucial specific factors such as frontage size, corner positioning, ease of accessibility, facing premiums (e.g., waterfront or park-facing), or differences in asking prices from the owners.',
  },
  {
    question: 'Does a high score guarantee investment returns?',
    answer:
      'While the Deal Score highlights strong investment fundamentals and identifies excellent market opportunities, actual financial returns depend on future economic shifts, developer strategies, and individual transaction timing. It is a powerful reference tool, not a guarantee.',
  },
  {
    question: 'How often are scores updated?',
    answer:
      "Scores are refreshed dynamically whenever there is a change in the listing's asking price, new surrounding infrastructure is announced, transaction averages shift in the neighborhood, or municipal approvals are recorded.",
  },
];

function AccordionItem({
  item,
  isOpen,
  onClick,
}: {
  item: FAQItem;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <div className="border border-slate-100 bg-white shadow-[0_2px_15px_-3px_rgba(0,0,0,0.02)] hover:border-slate-200 transition-colors duration-200">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-5 text-left focus:outline-none select-none group"
      >
        <span className="text-sm font-black text-slate-800 uppercase tracking-wide group-hover:text-[#006838] transition-colors duration-150 flex items-center gap-3">
          <HelpCircle size={16} className="text-[#82BC41] flex-shrink-0" />
          {item.question}
        </span>
        <ChevronDown
          size={18}
          className={`text-slate-400 group-hover:text-[#006838] transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180 text-[#006838]' : ''}`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[300px] border-t border-slate-50' : 'max-h-0'}`}
      >
        <p
          className="p-5"
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '15px',
            fontWeight: 300,
            lineHeight: '28.8px',
            color: 'rgb(80, 81, 79)',
          }}
        >
          {item.answer}
        </p>
      </div>
    </div>
  );
}

export default function DealScoreTemplate({
  page,
  meta,
  slug,
}: {
  page?: any;
  meta?: any;
  slug?: string;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    // Elegant micro-animation for the Deal Score preview card
    const targetScore = 87;
    const duration = 1500;
    const stepTime = Math.max(Math.floor(duration / targetScore), 15);
    let current = 0;

    const timer = setInterval(() => {
      current += 1;
      if (current >= targetScore) {
        clearInterval(timer);
        setAnimatedScore(targetScore);
      } else {
        setAnimatedScore(current);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, []);

  const pageTitle = page?.title || 'PlotAE Deal Score';
  const subtitle =
    meta?.banner_subtitle ||
    'Proprietary 100-point data-driven evaluation engine for land parcels in Dubai.';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Jost, sans-serif' }}>
      <Header />

      {/* 1. Page Header & Breadcrumbs */}
      <div className="pt-[110px] pb-10 bg-slate-50 border-b border-slate-100/80">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1
                className="text-3xl md:text-4xl font-black text-[#1a1a1a] uppercase tracking-tight"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {pageTitle}
              </h1>
              <div className="w-16 h-1 bg-[#006838] mt-2"></div>
            </div>

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
              <Link href="/" className="hover:text-[#006838] transition-colors">
                Home
              </Link>
              <span>/</span>
              <span className="text-slate-600 font-extrabold">Deal Score</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Panoramic Intro & Interactive Demo Block */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content (60%) */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
                Proprietary Algorithms
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] leading-tight uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                What is the PlotAE Deal Score?
              </h2>
              <div className="w-12 h-1 bg-[#82BC41] rounded"></div>

              <div
                className="space-y-4"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: 300,
                  lineHeight: '28.8px',
                  color: 'rgb(80, 81, 79)',
                }}
              >
                <p>
                  The <strong>PlotAE Deal Score</strong> is a smart rating system that helps
                  investors, developers, and brokers instantly assess the investment potential,
                  viability, and value of a land parcel in the UAE.
                </p>
                <p>
                  Every plot listed on PlotAE is analyzed across five critical parameters for a
                  total of 100 points. By transforming hundreds of complex market points into a
                  simple, single benchmark, we make land comparison seamless and enable smarter,
                  faster investment moves in seconds.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex gap-3 items-start">
                  <CheckCircle2 size={18} className="text-[#006838] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600 text-sm font-semibold">
                    100% data-driven and transparent metrics
                  </span>
                </div>
                <div className="flex gap-3 items-start">
                  <CheckCircle2 size={18} className="text-[#006838] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600 text-sm font-semibold">
                    Real-time benchmarked asking prices
                  </span>
                </div>
                <div className="flex gap-3 items-start">
                  <CheckCircle2 size={18} className="text-[#006838] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600 text-sm font-semibold">
                    Factoring future infrastructure & plans
                  </span>
                </div>
                <div className="flex gap-3 items-start">
                  <CheckCircle2 size={18} className="text-[#006838] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600 text-sm font-semibold">
                    RERA and DLD verified benchmarks
                  </span>
                </div>
              </div>
            </div>

            {/* Right Interactive Mockup Card (40%) */}
            <div className="lg:col-span-5">
              <div className="bg-gradient-to-br from-[#006838] to-[#004d2a] p-8 text-white shadow-[0_20px_50px_rgba(0,104,56,0.15)] border border-[#006838]/20 flex flex-col justify-between relative overflow-hidden group">
                <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/5 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-8 -left-8 w-36 h-36 bg-[#82BC41]/10 rounded-full blur-xl"></div>

                <div className="relative z-10 space-y-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-[10px] font-black text-[#82BC41] uppercase tracking-widest block">
                        PLOTAEx DEMO CARD
                      </span>
                      <h4
                        className="text-lg font-black uppercase tracking-wide"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        Business Bay Plot
                      </h4>
                    </div>
                    <span className="bg-white/10 text-white border border-white/20 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">
                      Residential G+19
                    </span>
                  </div>

                  {/* Circular Animated Score Counter */}
                  <div className="flex items-center gap-6 py-4">
                    <div className="relative w-28 h-28 flex items-center justify-center bg-white/5 border-4 border-[#82BC41] rounded-full shadow-inner">
                      <div className="text-center">
                        <span className="text-3xl md:text-4xl font-black tracking-tighter text-white">
                          {animatedScore}
                        </span>
                        <span className="text-[10px] font-bold text-[#82BC41] block tracking-widest mt-0.5">
                          /100
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <span className="inline-flex items-center gap-1.5 bg-[#82BC41]/20 text-[#82BC41] text-[10px] font-black uppercase tracking-widest px-2.5 py-1 border border-[#82BC41]/30">
                        <Award size={12} /> Excellent Deal
                      </span>
                      <p className="text-xs text-white/80 leading-relaxed font-medium">
                        This plot scores exceptionally high due to close metro access, a 12% price
                        advantage over community averages, and fully cleared legal deeds.
                      </p>
                    </div>
                  </div>

                  {/* Criteria Scores */}
                  <div className="border-t border-white/10 pt-4 space-y-2 text-xs">
                    <div className="flex justify-between items-center text-white/90">
                      <span className="font-semibold">Location Proximity</span>
                      <span className="font-black text-[#82BC41]">23 / 25</span>
                    </div>
                    <div className="flex justify-between items-center text-white/90">
                      <span className="font-semibold">Price Advantage (vs Avg)</span>
                      <span className="font-black text-[#82BC41]">18 / 20</span>
                    </div>
                    <div className="flex justify-between items-center text-white/90">
                      <span className="font-semibold">Future Area Growth</span>
                      <span className="font-black text-[#82BC41]">17 / 20</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. The 5 pillars Cards Grid */}
      <section className="py-20 bg-slate-50 border-y border-slate-100">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
              Evaluation Metrics
            </span>
            <h2
              className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              The Five-Point Score System
            </h2>
            <p className="text-slate-500 text-sm font-semibold max-w-lg mx-auto">
              Our comprehensive algorithm divides the final score into 5 crucial parameters to give
              you full visibility.
            </p>
            <div className="w-16 h-1 bg-[#82BC41] mx-auto rounded"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* 1. Location */}
            <div className="bg-white p-6 border border-slate-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#006838]/5 text-[#006838] group-hover:bg-[#006838] group-hover:text-white transition-colors duration-300 flex items-center justify-center rounded-none">
                  <MapPin size={22} />
                </div>
                <h3
                  className="text-base font-black text-slate-800 uppercase tracking-wide"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  1. Location
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed font-medium">
                  Measures absolute connectivity, road accessibility, and proximity to essential
                  urban assets like schools, healthcare, metro lines, and retail points.
                </p>
              </div>
              <div className="border-t border-slate-100 pt-4 mt-6 flex justify-between items-center text-xs font-black">
                <span className="text-slate-400">MAX POINTS</span>
                <span className="text-[#006838] bg-[#006838]/10 px-2.5 py-1">25 PTS</span>
              </div>
            </div>

            {/* 2. Growth */}
            <div className="bg-white p-6 border border-slate-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#006838]/5 text-[#006838] group-hover:bg-[#006838] group-hover:text-white transition-colors duration-300 flex items-center justify-center rounded-none">
                  <TrendingUp size={22} />
                </div>
                <h3
                  className="text-base font-black text-slate-800 uppercase tracking-wide"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  2. Growth
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed font-medium">
                  Assesses future area potential by tracking surrounding government infrastructure
                  plans, nearby mega-developments, and active transaction velocity.
                </p>
              </div>
              <div className="border-t border-slate-100 pt-4 mt-6 flex justify-between items-center text-xs font-black">
                <span className="text-slate-400">MAX POINTS</span>
                <span className="text-[#006838] bg-[#006838]/10 px-2.5 py-1">20 PTS</span>
              </div>
            </div>

            {/* 3. Legal */}
            <div className="bg-white p-6 border border-slate-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#006838]/5 text-[#006838] group-hover:bg-[#006838] group-hover:text-white transition-colors duration-300 flex items-center justify-center rounded-none">
                  <ShieldCheck size={22} />
                </div>
                <h3
                  className="text-base font-black text-slate-800 uppercase tracking-wide"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  3. Legal & Docs
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed font-medium">
                  Guarantees security. Validates title deed records, reviews RERA compliance status,
                  and ensures zero pending municipal encumbrances or flags.
                </p>
              </div>
              <div className="border-t border-slate-100 pt-4 mt-6 flex justify-between items-center text-xs font-black">
                <span className="text-slate-400">MAX POINTS</span>
                <span className="text-[#006838] bg-[#006838]/10 px-2.5 py-1">20 PTS</span>
              </div>
            </div>

            {/* 4. Price Advantage */}
            <div className="bg-white p-6 border border-slate-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#006838]/5 text-[#006838] group-hover:bg-[#006838] group-hover:text-white transition-colors duration-300 flex items-center justify-center rounded-none">
                  <BadgeDollarSign size={22} />
                </div>
                <h3
                  className="text-base font-black text-slate-800 uppercase tracking-wide"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  4. Price Value
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed font-medium">
                  Compares the exact asking rate per square foot against recent community
                  transactions and historical neighborhood data to isolate immediate value plays.
                </p>
              </div>
              <div className="border-t border-slate-100 pt-4 mt-6 flex justify-between items-center text-xs font-black">
                <span className="text-slate-400">MAX POINTS</span>
                <span className="text-[#006838] bg-[#006838]/10 px-2.5 py-1">20 PTS</span>
              </div>
            </div>

            {/* 5. Uniqueness */}
            <div className="bg-white p-6 border border-slate-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#006838]/5 text-[#006838] group-hover:bg-[#006838] group-hover:text-white transition-colors duration-300 flex items-center justify-center rounded-none">
                  <Sparkles size={22} />
                </div>
                <h3
                  className="text-base font-black text-slate-800 uppercase tracking-wide"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  5. Uniqueness
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed font-medium">
                  Analyzes distinct value multipliers such as corner positioning, main boulevard
                  road frontage, waterfront views, or high active demand.
                </p>
              </div>
              <div className="border-t border-slate-100 pt-4 mt-6 flex justify-between items-center text-xs font-black">
                <span className="text-slate-400">MAX POINTS</span>
                <span className="text-[#006838] bg-[#006838]/10 px-2.5 py-1">15 PTS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Score Ranges Explanation Dashboard */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content Column (50%) */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
                How to read scores
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase leading-tight"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Understanding the Score Bands
              </h2>
              <div className="w-12 h-1 bg-[#82BC41] rounded"></div>
              <p
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: 300,
                  lineHeight: '28.8px',
                  color: 'rgb(80, 81, 79)',
                }}
              >
                Our algorithm categorizes every land listing in the UAE into four clear transaction
                tiers. These bands allow institutional developers and private buyers alike to screen
                deals in seconds.
              </p>

              <div className="bg-slate-50 p-6 border-l-4 border-[#006838] text-xs space-y-2">
                <span className="font-black text-[#006838] uppercase tracking-wider block">
                  PRO TIP FOR BUYERS
                </span>
                <p className="text-slate-500 leading-relaxed font-medium">
                  A high Deal Score represents superb fundamental value. However, we always
                  recommend conducting independent site surveys to match a plot's unique traits to
                  your development goals.
                </p>
              </div>
            </div>

            {/* Right Gauge Bands Column (70%) */}
            <div className="lg:col-span-7 space-y-4">
              {/* Excellent Tier */}
              <div className="flex items-center gap-6 p-5 border border-slate-100 bg-[#006838]/5 shadow-sm">
                <div className="w-20 h-20 rounded-none bg-[#006838] text-white flex flex-col justify-center items-center flex-shrink-0 select-none">
                  <span className="text-lg font-black tracking-tight">80-100</span>
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-wide">
                    Excellent Deal
                  </h4>
                  <p className="text-slate-600 text-xs leading-relaxed font-medium">
                    Maximum investment potential. Features strong pricing advantages, fully cleared
                    legal documentation, and prime location access with strong growth drivers.
                  </p>
                </div>
              </div>

              {/* Good Tier */}
              <div className="flex items-center gap-6 p-5 border border-slate-100 bg-white shadow-sm">
                <div className="w-20 h-20 rounded-none bg-[#82BC41] text-white flex flex-col justify-center items-center flex-shrink-0 select-none">
                  <span className="text-lg font-black tracking-tight">60-79</span>
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-wide">
                    Good Deal
                  </h4>
                  <p className="text-slate-600 text-xs leading-relaxed font-medium">
                    Solid parameters. Healthy, transparent metrics matching standard market prices
                    with highly stable growth prospects in major communities.
                  </p>
                </div>
              </div>

              {/* Fair Tier */}
              <div className="flex items-center gap-6 p-5 border border-slate-100 bg-white shadow-sm">
                <div className="w-20 h-20 rounded-none bg-orange-400 text-white flex flex-col justify-center items-center flex-shrink-0 select-none">
                  <span className="text-lg font-black tracking-tight">40-59</span>
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-wide">
                    Fair Deal (Caution)
                  </h4>
                  <p className="text-slate-600 text-xs leading-relaxed font-medium">
                    Average value. Might present minor pricing premiums over local trends, slower
                    neighborhood growth cycles, or requires detailed zoning review.
                  </p>
                </div>
              </div>

              {/* Weak Tier */}
              <div className="flex items-center gap-6 p-5 border border-slate-100 bg-white shadow-sm">
                <div className="w-20 h-20 rounded-none bg-red-500 text-white flex flex-col justify-center items-center flex-shrink-0 select-none">
                  <span className="text-lg font-black tracking-tight">&lt; 40</span>
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-wide">
                    Weak Deal
                  </h4>
                  <p className="text-slate-600 text-xs leading-relaxed font-medium">
                    High risk or significantly overpriced. Poorly benchmarked asking rates, lack of
                    infrastructural access, or ambiguous legal document details.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Interactive FAQ Section */}
      <section className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1000px' }}>
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
              Investor Inquiries
            </span>
            <h2
              className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Frequently Asked Questions
            </h2>
            <div className="w-16 h-1 bg-[#82BC41] mx-auto rounded"></div>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                item={faq}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 6. Dynamic CTA Block */}
      <section className="py-20 bg-gradient-to-br from-[#006838] to-[#004d2a] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.05),transparent)]"></div>
        <div className="container mx-auto px-6 text-center max-w-3xl relative z-10 space-y-6">
          <span className="text-[#82BC41] text-xs font-black uppercase tracking-[0.3em] block">
            Unlock High-Scoring Deals
          </span>
          <h2
            className="text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Ready to find premium land investments in Dubai?
          </h2>
          <p className="text-white/80 text-sm leading-relaxed max-w-lg mx-auto font-medium">
            Explore live land listings and view actual PlotAE Deal Scores on verified properties
            across Business Bay, Sports City, and Meydan.
          </p>

          <div className="pt-4 flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              href="/luxury-properties"
              className="inline-flex items-center gap-2 bg-[#82BC41] hover:bg-white text-slate-900 hover:text-[#006838] font-black text-xs uppercase tracking-widest px-8 py-4 transition-all duration-300 rounded-none shadow-lg w-full sm:w-auto justify-center group"
            >
              Explore Live Plots
              <ArrowRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </Link>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 border border-white/30 hover:border-white bg-white/5 hover:bg-white/10 text-white font-black text-xs uppercase tracking-widest px-8 py-4 transition-all duration-300 rounded-none w-full sm:w-auto justify-center"
            >
              Request Advisory
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
